import type { Dispatch, ReactNode, SetStateAction } from 'react';
import { createContext, useContext, useMemo, useState } from 'react';

type OpenPaths = Partial<Record<number, string[]>>;

interface NavigationContextType {
  openPaths: OpenPaths;
  setOpenPaths: Dispatch<SetStateAction<OpenPaths>>;
  setOpenMenu: Dispatch<SetStateAction<boolean>>;
  isMenuOpen: boolean;
  open: () => void;
  /** Full reset: collapses sidebar and clears openPaths. Called after navigation to a leaf route. */
  close: () => void;
  /** Collapses/expands the sidebar. Resets openPaths so auto-open picks the correct submenu on re-open. */
  toggle: () => void;
}

const NavigationContext = createContext<NavigationContextType | undefined>(undefined);

interface NavigationProviderProps {
  children: ReactNode;
}

export const NavigationProvider = ({ children }: NavigationProviderProps) => {
  const [openPaths, setOpenPaths] = useState<OpenPaths>({});
  const [openMenu, setOpenMenu] = useState<boolean>(false);

  const value = useMemo(
    () => ({
      openPaths,
      setOpenPaths,
      setOpenMenu,
      isMenuOpen: openMenu,
      open: () => {
        setOpenMenu(true);
      },
      close: () => {
        setOpenMenu(false);
        setOpenPaths({});
      },
      toggle: () => {
        setOpenMenu(prev => !prev);
        setOpenPaths({});
      }
    }),
    [openMenu, openPaths]
  );

  return <NavigationContext value={value}>{children}</NavigationContext>;
};

export const useNavigationContext = () => {
  const context = useContext(NavigationContext);
  if (!context) throw new Error('useNavigationContext must be used within NavigationProvider');
  return context;
};
